import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ENV } from "@app/env";
import { Store } from "@ngrx/store";
import { AngularFireDatabase } from "angularfire2/database";
import { AngularFirestore } from "angularfire2/firestore";
import * as firebase from "firebase/app";
import { Observable } from "rxjs/Observable";
import { of } from "rxjs/observable/of";
import { switchMap, take } from "rxjs/operators";
import { catchError, retry } from "rxjs/operators";
import { IClient } from "../shared/models/client.model";
import { IEventQuestion } from "../shared/models/event-questions.model";
import { IPushMessage } from "../shared/models/messages.model";
import * as sharedSelectors from "../shared/reducers/shared.reducer";
import { HistoryData } from "./history";
import { _TRACKING_ENTITIES, TrackingProvider } from "./tracking";
import { Utilities } from "./utilities";
import { isValidEventId } from "../helpers/utils.helper";
import { get } from "lodash";
import moment from "moment";
import { AuthData } from "./auth";

@Injectable()
export class EventData {
  private action: string = "update";

  constructor(
    private afDb: AngularFireDatabase,
    private historyData: HistoryData,
    private tracking: TrackingProvider,
    private http: HttpClient,
    private store: Store<{}>,
    private afs: AngularFirestore,
    private utilities: Utilities,
    private authData: AuthData
  ) { }

  public getEvents(client: string): any {
    return this.afDb.list(`Clients/${client}/EventConfig`).valueChanges();
  }

  public getConnections(client: string): any {
    console.log(`Clients/${client}/___connections`)
    return this.afDb.list(`Clients/${client}/___connections`, ref => ref.limitToFirst(200)).valueChanges();
  }

  public getEventsNoSubEvent(client: string): Observable<IClient[]> {
    return this.afDb.list<IClient>(
      `Clients/${client}/EventConfig`,
      (ref) => ref.orderByChild("isSubevent").equalTo(false),
    ).valueChanges();
  }

  public getCategorieStaffData(clientId: string, categorieId: string) {
    return this.afDb.object<any>(`/Clients/${clientId}/StaffCategories/${categorieId}`)
      .valueChanges();
  }

  public getEventsSubEvent(client: string, eventId: string): Observable<IClient[]> {
    return this.afDb.list<IClient>(
      `Clients/${client}/EventConfig`,
      (ref) => ref.orderByChild("eventId").equalTo(eventId),
    ).valueChanges();
  }

  public getEventInfo(client: string, eventId: string): Observable<any> {
    if (this.utilities.validateEventIdParam(eventId)) {
      return this.afDb.object(`Clients/${client}/EventConfig/${eventId}`).valueChanges();
    }
    return of([]);
  }

  public getEventInfoSnap(client: string, eventId: string): Promise<any> {
    return this.afDb.object(`Clients/${client}/EventConfig/${eventId}`)
      .query.once("value").then((snap) => snap.val());
  }

  public getEventUsers(client: string, id: string): Observable<any> {
    return this.afDb.list(`Clients/${client}/Event/${id}/Users`).valueChanges();
  }

  public getEventModules(client: string, id: string): any {
    return this.afDb
      .object(`Clients/${client}/EventConfig/${id}/modules`)
      .valueChanges();
  }

  public getEventAgendas(eventId: string): Observable<any> {
    return this.store.select(sharedSelectors.getClientId).pipe(
      switchMap((clientId) => {
        if (clientId) {
          return this.afDb
            .object(`Clients/${clientId}/Event/${eventId}/Agenda`)
            .valueChanges();
        } else {
          return of([]);
        }
      }),
    );
  }

  public getEventAgendaConfig(eventId: string): Observable<any> {
    return this.store.select(sharedSelectors.getClientId).pipe(
      switchMap((clientId) => {
        if (clientId) {
          return this.afDb
            .object(`Clients/${clientId}/Event/${eventId}/AgendaConfig`)
            .valueChanges();
        } else {
          return of();
        }
      }),
    );
  }

  public getEventGenericConfig(eventId: string, moduleName: string): Observable<any> {
    return this.store.select(sharedSelectors.getClientId).pipe(
      switchMap((clientId) => {
        if (clientId) {
          let base = `Clients/${clientId}/Event/${eventId}/${moduleName}`
          if (!this.utilities.validateEventIdParam(eventId)) {
            base = `Clients/${clientId}/Interactivity/${moduleName}`
          }
          return this.afDb
            .object(base)
            .valueChanges();
        } else {
          return of();
        }
      }),
    );
  }

  public createOrEditConfigAgenda(eventId: string, agendaInfo: any): Promise<any> {
    return this.store.select(sharedSelectors.getClientId).pipe(
      switchMap((clientId) => {
        let base = `Clients/${clientId}/Event/${eventId}/AgendaConfig`
        if (!this.utilities.validateEventIdParam(eventId)) {
          base = `Clients/${clientId}/Interactivity/AgendaConfig`
        }
        return this.afDb.database
          .ref(base)
          .set(agendaInfo)
      }),
      take(1),
    ).toPromise();
  }

  public createOrEditConfigGeneric(eventId: string, info: any, moduleName: string): Promise<any> {
    return this.store.select(sharedSelectors.getClientId).pipe(
      switchMap((clientId) => {
        let base = `Clients/${clientId}/Event/${eventId}/${moduleName}`
        if (!this.utilities.validateEventIdParam(eventId)) {
          base = `Clients/${clientId}/Interactivity/${moduleName}`
        }
        return this.afDb.database
          .ref(base)
          .set(info)
      }),
      take(1),
    ).toPromise();
  }

  public getEventInteractivity(client: string, id: string): Observable<any> {
    return this.afDb
      .object(`Clients/${client}/EventConfig/${id}/interactivity`)
      .valueChanges();
  }

  public getEventModule(client: string, id: string, module: string): any {
    let base = `Clients/${client}/Event/${id}/${module}`
    if (!this.utilities.validateEventIdParam(id)) {
      base = `Clients/${client}/Interactivity/${module}`
    }
    return this.afDb
      .object(base)
      .valueChanges();
  }

  public getEventConfigModule(client: string, id: string, module: string): any {
    let reference = `Clients/${client}/EventConfig/${id}/modules_config/${module}`;
    return this.afDb.object(reference).valueChanges();
  }

  public getEventModuleList(client: string, id: string, module: string): Observable<any> {
    let base = `Clients/${client}/Event/${id}/${module}`
    if (!this.utilities.validateEventIdParam(id)) {
      base = `Clients/${client}/Interactivity/${module}`
    }
    return this.afDb
      .list(base)
      .valueChanges();
  }

  public getEventModuleListSnap(client: string, id: string, module: string) {
    let base = `Clients/${client}/Event/${id}/${module}`
    if (!this.utilities.validateEventIdParam(id)) {
      base = `Clients/${client}/Interactivity/${module}`
    }
    return this.afDb
      .list(base).valueChanges();
  }

  public getEventMessages(
    client: string,
    eventId: string,
    module: "Alert" | "Toast" | "Push" | "Footer",
  ): Observable<any[]> {
    if (isValidEventId(eventId)) {
      return this.afDb
        .list(`Clients/${client}/Event/${eventId}/Messages/${module}`)
        .valueChanges();
    } else {
      return this.afDb
        .list(`Clients/${client}/Interactivity/Messages/${module}`)
        .valueChanges();
    }
  }

  public getEventSecurity(client: string, id: string): any {
    return this.afDb
      .object(`Clients/${client}/EventList/${id}/permissions`)
      .valueChanges();
  }

  public getEventLanguage(client: string, id: string): any {
    return this.afDb
      .object(`Clients/${client}/EventList/${id}/defaultLanguage`)
      .valueChanges();
  }

  public getEventData(client: string, id: string): any {
    return this.afDb.object(`Clients/${client}/Event/${id}`).valueChanges();
  }

  public getEventCategories(clientId: string, id: string): any {
    return this.afDb.object(`Clients/${clientId}/EventConfig/${id}/categories`).valueChanges();
  }

  // transactionContentModule(
  //   client: string,
  //   id: string,
  //   module: string,
  //   state: boolean
  // ) {
  //   return firebase
  //     .database()
  //     .ref(`Clients/${client}/EventConfig/${id}/modules_content/${module}`)
  //     .transaction(current => {
  //       return state;
  //     });
  // }

  public transactionModule(
    client: string,
    id: string,
    module: string,
    state: boolean,
  ) {
    return firebase
      .database()
      .ref(`Clients/${client}/EventConfig/${id}/modules/${module}`)
      .transaction((current) => {
        return state;
      });
  }

  public createEventMessageStatitics(
    clientId: string,
    eventId: string,
    module: string,
    messageId: string,
  ): Observable<any> {
    return Observable.fromPromise(
      this.afs.firestore.collection(`${clientId}`)
        .doc("messages-event")
        .collection("event")
        .doc(eventId)
        .collection(module)
        .doc(`${messageId}`)
        .set({ views: 0 }));
  }

  public statisticEventMessageNodeExist(clientId: string, eventId: string, module: string, messageId: string) {
    return this.afs.collection(`${clientId}`)
      .doc("messages-event")
      .collection("event")
      .doc(eventId)
      .collection(module)
      .doc(`${messageId}`)
      .valueChanges();
  }

  public checkEventMessageStatisticNode(clientId: string, eventId: string, module: string, messageId: string) {
    return this.statisticEventMessageNodeExist(clientId, eventId, module, messageId).pipe(
      (switchMap((statistic) => {
        if (!statistic) {
          return this.createEventMessageStatitics(clientId, eventId, module, messageId);
        } else {
          return Observable.empty();
        }
      })),
    );
  }

  public setStateTrueMessageNotification(
    client: string,
    idEvent: string,
    idMessage: string,
    module: string
  ) {
    let reference = `/Clients/${client}/Interactivity/Messages/${module}/${idMessage}`;

    if (isValidEventId(idEvent)) {
      reference = `/Clients/${client}/Event/${idEvent}/Messages/${module}/${idMessage}`;
    }
    return this.afDb.object(reference).update({ state: true });
  }

  public setStateTrueMessageNotificationFooter(
    client: string,
    idEvent: string,
    idMessage: string,
    module: string,
    state: boolean
  ) {
    let reference = `/Clients/${client}/Interactivity/Messages/${module}/${idMessage}`;

    if (isValidEventId(idEvent)) {
      reference = `/Clients/${client}/Event/${idEvent}/Messages/${module}/${idMessage}`;
    }
    return this.afDb.object(reference).update({ state: !state });
  }

  public ofAllStateEventMessageModule(client: string, idEvent: string, module: string) {
    let ref = `/Clients/${client}/Interactivity/Messages/${module}`
    if (this.utilities.validateEventIdParam(idEvent)) {
      ref = `/Clients/${client}/Event/${idEvent}/Messages/${module}`
    }

    return this.afDb.list<any>(ref).query.once('value').then(snap => snap.val());
  }

  public setStateFalseMessageNotification(
    clientId: string,
    messageId: string,
    eventId: string,
    moduleAlert: string) {

    let reference = `/Clients/${clientId}/Interactivity/Messages/${moduleAlert}/${messageId}`;

    if (isValidEventId(eventId)) {
      reference = `/Clients/${clientId}/Event/${eventId}/Messages/${moduleAlert}/${messageId}`;
    }
    return this.afDb.object(reference).update({ state: false });
  }

  public createOrUpdateMessaje(
    client: string,
    idEvent: string,
    module: string,
    object: any,
    idMessage: string = "",
  ) {
    if (!idMessage) {
      idMessage = this.afDb.createPushId();
      this.action = "create";
    }

    object.id = idMessage;

    return new Promise((res, rej) => {
      let reference = `/Clients/${client}/Interactivity/Messages/${module}`;
      if (isValidEventId(idEvent)) {
        reference = `/Clients/${client}/Event/${idEvent}/Messages/${module}`;
      }

      this.afDb.database
        .ref(`${reference}/${idMessage}`)
        .update(object)
        .then(() => {
          return res(idMessage);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public createEventPushMessage(clientId: string, eventId: string, message: any, send = false) {
    if (!message.id) {
      message.id = this.afDb.createPushId();
      this.action = "create";
    }
    if (message.date) {
      if (!send) {
        message.date = moment(message.date).utc().format('YYYY-MM-DD HH:mm:ss');
      }
    }

    let ref = `Clients/${clientId}/Interactivity/Messages/Push/${message.id}`;
    if (this.utilities.validateEventIdParam(eventId)) {
      ref = `Clients/${clientId}/Event/${eventId}/Messages/Push/${message.id}`;
    }

    return new Promise((resolve, reject) => {
      this.afDb.database
        .ref(ref)
        .set({ ...message, clientId })
        .then(() => resolve())
        .catch((error) => {
          console.error("A big error happen");
          return reject(error);
        });
    });
  }

  public programedPush(message) {
    const send = {
      "message": {
        "id": message.id,
        "notification": {
          "title": message.title,
          "body": message.body
        },
        "tokens": message.tokens || [],
        "topic": message.topic,
        "data": {
          "url": message.url,
        }
      },
      "type": message.type,
      "source": "ADMINISTRATOR REBUS",
      "sendAt": get(message, 'date', 0)
    };
    return this.http.post(`${ENV.notifications_url}/notifications`, send).toPromise();
  }

  public sendEventPushMessage(clientConfig: IClient, eventId: string, message: any) {
    let ref = `Clients/${clientConfig.id}/Interactivity/Messages/Push/${message.id}/sended`;
    if (this.utilities.validateEventIdParam(eventId)) {
      ref = `Clients/${clientConfig.id}/Event/${eventId}/Messages/Push/${message.id}/sended`;
    }

    return this.programedPush(message).then(() => this.afDb.object(ref).set(true));
  }

  public deletePushMessage(idMessage) {
    return this.http.delete(`${ENV.notifications_url}/notifications/${idMessage}`).toPromise();
  }

  public deletePushMessageFirebase(clientId, eventId, message) {
    let ref = `Clients/${clientId}/Interactivity/Messages/Push/${message.id}`;
    if (this.utilities.validateEventIdParam(eventId)) {
      ref = `Clients/${clientId}/Event/${eventId}/Messages/Push/${message.id}`;
    }
    return new Promise((resolve, reject) => {
      this.afDb.object(ref)
        .remove()
        .then(() => resolve())
        .catch((error) => {
          console.error("A big error happen");
          return reject(error);
        });
    });
  }

  public setEventDataFirst(client: string, object: any, eventId: string = "", formSelectModules = null): any {

    if (formSelectModules.modules) {
      object.modules = formSelectModules.modules
    }

    if (!eventId) {
      eventId = this.afDb.createPushId();
      object.created_at = new Date().getTime();
      this.action = "create";
    } else {
      object.update_at = new Date().getTime();
    }

    object.id = eventId;

    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/EventConfig/${eventId}`)
        .update(object)
        .then(() => {
          this.historyData.set(
            "General description",
            "event",
            this.action,
            object,
          );

          return res(eventId);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public setEventDataInteractivity(client: string, object: any, eventId: string): any {
    if (object.Survey === undefined) { object.Survey = ""; }
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/EventConfig/${eventId}/interactivity`)
        // TODO: Fix resolve .set to .update
        .set(object)
        .then(() => {
          this.historyData.set(
            "Interactivity",
            "event",
            this.action,
            object,
          );

          return res(eventId);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public setEventRegisterCode(client: string, object: any, eventId: string): any {
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/EventUsers/${eventId}/RegisterCode`)
        // TODO: Fix resolve .set to .update
        .set(object)
        .then(() => {
          return res(eventId);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public pushEventModule(
    client: string,
    object: any,
    eventId: string,
    module: string,
    id: string = "",
  ): any {
    if (id === "") {
      this.action = "create";
      id = this.afDb.createPushId();
      object.created_at = new Date().getTime();
    } else {
      object.update_at = new Date().getTime();
    }

    object.id = id;

    return new Promise((res, rej) => {
      let base = `Clients/${client}/Event/${eventId}/${module}/${id}`
      if (!this.utilities.validateEventIdParam(eventId)) {
        base = `Clients/${client}/Interactivity/${module}/${id}`
      }

      this.afDb.database
        .ref(base)
        .update(object)
        .then(() => {
          this.historyData.set(
            `Module ${module} content`,
            "event",
            this.action,
            object,
          );

          if (module === "News") {
            this.tracking.trackEntity(_TRACKING_ENTITIES.news, id, client, eventId, object, "Tracking en la cracion/actualizacion de noticias");
          }

          return res(id);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public setEventModule(
    client: string,
    object: any,
    eventId: string,
    module: string,
  ): any {
    return new Promise((res, rej) => {
      let base = `Clients/${client}/Event/${eventId}/${module}`
      if (!this.utilities.validateEventIdParam(eventId)) {
        base = `Clients/${client}/Interactivity/${module}`
      }
      this.afDb.database
        .ref(base)
        .update(object)
        .then(() => {
          // this.historyData.set(
          //   `Module ${module} content`,
          //   "event",
          //   this.action,
          //   object,
          // );

          return res(eventId);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public setEventCategories(client: string, categories: any, eventId: string): Promise<any> {
    return this.afDb.database
      .ref(`Clients/${client}/EventConfig/${eventId}/categories`)
      .update(categories);
  }

  public updateEventAI(client: string, eventId: string, eventData: any): Promise<any> {
    return this.tracking.trackEntityPromise(_TRACKING_ENTITIES.event, eventId, client, eventId, eventData, "Tracking cuando se invoca en un evento");
  }

  public setEventConfigModule(
    client: string,
    object: any,
    eventId: string,
    module: string,
  ): any {
    return new Promise((res, rej) => {
      let reference = `Clients/${client}/EventConfig/${eventId}/modules_config/${module}`;
      this.afDb.database
        .ref(reference)
        .update({
          ...object,
          id: module,
        })
        .then(() => {
          return res(eventId);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public createEventPollStatitics(clientId: string, eventId: string, pollId: string): Observable<any> {
    return Observable.fromPromise(
      this.afs.firestore.collection(`${clientId}`)
        .doc("statistics-event")
        .collection("event")
        .doc(eventId)
        .collection("poll")
        .doc(`${pollId}`)
        .set({ views: 0 }));
  }

  public statisticEventPollNodeExist(clientId: string, eventId: string, pollId: string) {
    return this.afs.collection(`${clientId}`)
      .doc("statistics-event")
      .collection("event")
      .doc(eventId)
      .collection("poll")
      .doc(`${pollId}`)
      .valueChanges();
  }

  public checkEventPollStatisticNode(clientId: string, eventId: string, pollId: string): Observable<any> {
    return this.statisticEventPollNodeExist(clientId, eventId, pollId)
      .pipe((switchMap((statistic) => {
        if (!statistic) {
          return this.createEventPollStatitics(clientId, eventId, pollId);
        }
        return Observable.of({});
      })),
      );
  }

  public setEventDataSecond(client: string, object: any, eventId: string): any {
    if (object.interactivity.Survey === undefined) { object.interactivity.Survey = ""; }
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/EventConfig/${eventId}`)
        .update(object)
        .then(() => {
          this.historyData.set(`Modules state`, "event", this.action, object);

          return res(eventId);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public setEventDataThirdh(client: string, object: any, eventId: string): any {
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/EventList/${eventId}/permissions`)
        // TODO: Fix resolve .set to .update
        .set(object)
        .then(() => {
          this.historyData.set(
            `Permissions state`,
            "event",
            this.action,
            object,
          );

          return res(eventId);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public getQuestionsByEventId(clientId: string, eventId: string): Observable<any[]> {
    return this.afDb
      .list<any>(`Clients/${clientId}/Event/${eventId}/Question/`)
      .valueChanges();
  }

  public getQuestionsByEventIdPublic(clientId: string, eventId: string): Observable<IEventQuestion[]> {
    return this.afDb
      .list<IEventQuestion>(`Clients/${clientId}/Event/${eventId}/Question/`).valueChanges();
  }

  public setQuestionByEventId(
    clientId: string,
    eventId: string,
    questionId: string,
    question: any,
  ) {
    question.created_at = new Date().getTime();
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${clientId}/Event/${eventId}/Question/${questionId}/`)
        .update(question)
        .then(() => {
          return res();
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public setQuestionArchiveByEventId(clientId: string, eventId: string, questionId: string) {
    return this.afDb.object(`/Clients/${clientId}/Event/${eventId}/Question/${questionId}/inactive`).set(true);
  }

  public unSetQuestionArchiveByEventId(clientId: string, eventId: string, questionId: string) {
    return this.afDb.object(`/Clients/${clientId}/Event/${eventId}/Question/${questionId}/inactive`).set("");
  }

  public deleteQuestionEventId(clientId: string, eventId: string, questionId: string) {
    return this.afDb.object(`/Clients/${clientId}/Event/${eventId}/Question/${questionId}`).remove();
  }

  public deleteUsersEventPrivate(clientId, eventId) {
    return this.http.delete(`${ENV.backend}/users/backup/${clientId}/${eventId}`).toPromise()
  }


  public sendReportQuestions(client: IClient, emailUser: string, eventId: string, eventName: string): Observable<any> {
    return this.authData.getTokenUser().pipe(
      switchMap((token: string) => {
        if (!token) {
          return of([]);
        }
        const headers = new HttpHeaders({ Authorization: token });

        const subject = eventId === '--' ?
          'Report de preguntas realizadas en: ' + ' el cliente: ' + client.name :
          'Reporte de preguntas realizadas en el evento: ' + eventName + ' del cliente: ' + client.name;

        // return this.http.post(`${ENV.jobs_url}/v1/reports/questions`,
        return this.http.post(`${ENV.backend}/reports/generate_questions`,
          {
            "clientId": client.id,
            "eventId": eventId,
            "zone": -5,
            "email": emailUser,
            "subject": subject
          },
          { headers });
      }),
    );
  }

}
// end file



// WEBPACK FOOTER //
// ./src/providers/event.ts